// src/analytics/GlobalAnalyticsTracker.tsx
import { useAnalytics } from 'context/AnalyticsContext';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { allRoutes } from 'routes/AllRoutes';
import { selectToken, selectUserData } from 'store/auth/selectors';
import { camelCaseToWords, getRoleFromToken } from 'utils/helpers';

export const GlobalAnalyticsTracker = () => {
  const { analytics } = useAnalytics();
  const { pathname, search } = useLocation();
  const token = useSelector(selectToken);
  const user = useSelector(selectUserData);

  const hasRunLifecycleEvents = useRef(false);
  const hasUserIdentified = useRef(false);

  const userRole = getRoleFromToken(token);

  const trackPage = () => {
    if (!analytics) return;
    const matchedRoute = allRoutes.find((route) =>
      matchPath({ path: route.path, end: true }, pathname),
    );

    const routeName =
      (pathname === '/login' ? 'Login Page' : matchedRoute?.key) ||
      'Unknown Page';

    const routeGroup = pathname?.split('/')?.[1] || '';
    analytics?.page(routeGroup, camelCaseToWords(routeName), {
      search,
    });
  };

  useEffect(() => {
    if (!analytics) return;
    if (!hasRunLifecycleEvents.current || !hasUserIdentified.current) {
      const runInitialSequence = async () => {
        if (user.userId && !hasUserIdentified.current) {
          analytics.identify(user.userEmail, {
            id: user.userId,
            country: user.userCountry,
            name: user.userName,
            role: userRole,
            email: user.userEmail,
          });
          hasUserIdentified.current = true;
        }

        if (
          (!user.userId || hasUserIdentified.current) &&
          !hasRunLifecycleEvents.current
        ) {
          analytics.track('App Opened');
          hasRunLifecycleEvents.current = true;
        }

        trackPage();
      };

      runInitialSequence();
    } else {
      trackPage();
    }
  }, [analytics, user, pathname]);

  return null;
};
